(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    api.install(root, root.document);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // Contract with Kite Page Load Custom JS: dispatch only after a confirmed
  // Dynamic BOGO cart mutation. Normal theme cart actions do not emit this.
  const EVENT_NAME = 'kite:dynamic-bogo:cart-mutated';
  const MAIN_CART_ID_PREFIX = 'MainCart-';
  const DRAWER_MAX_CHECKS = 25;
  const DRAWER_CHECK_INTERVAL_MS = 100;

  // Compare cart state by aggregated variant quantities instead of row count.
  // The same variant can appear in more than one discounted cart line.
  function aggregateSignature(items, getVariantId, getQuantity) {
    const quantities = new Map();

    Array.from(items || []).forEach(function (item) {
      const variantId = String(getVariantId(item) || '').trim();
      const quantity = Number(getQuantity(item));

      if (!/^\d+$/.test(variantId) || !Number.isFinite(quantity) || quantity <= 0) return;

      quantities.set(variantId, (quantities.get(variantId) || 0) + quantity);
    });

    return Array.from(quantities.entries())
      .sort(function (left, right) {
        return left[0].localeCompare(right[0]);
      })
      .map(function (entry) {
        return entry[0] + ':' + entry[1];
      })
      .join('|');
  }

  function cartSignature(cart) {
    return aggregateSignature(
      cart && cart.items,
      function (item) {
        return item && item.variant_id;
      },
      function (item) {
        return item && item.quantity;
      }
    );
  }

  function itemSignature(items) {
    return aggregateSignature(
      items,
      function (item) {
        return item && item.variantId;
      },
      function (item) {
        return item && item.quantity;
      }
    );
  }

  function mainCartSectionId(mainCart) {
    const id = mainCart && typeof mainCart.id === 'string' ? mainCart.id : '';
    return id.indexOf(MAIN_CART_ID_PREFIX) === 0 ? id.slice(MAIN_CART_ID_PREFIX.length) : '';
  }

  function sleep(delay) {
    return new Promise(function (resolve) {
      setTimeout(resolve, delay);
    });
  }

  async function syncDrawer(dependencies) {
    const options = dependencies || {};
    const isCurrent = options.isCurrent;
    const readCart = options.readCart;
    const readDrawerSignature = options.readDrawerSignature;
    const wait = options.wait || sleep;
    const dispatchRefresh = options.dispatchRefresh;
    const maxChecks = options.maxChecks || DRAWER_MAX_CHECKS;
    const intervalMs = options.intervalMs || DRAWER_CHECK_INTERVAL_MS;

    try {
      if (typeof isCurrent !== 'function' || !isCurrent(options.generation)) return 'stale';
      if (
        typeof readCart !== 'function' ||
        typeof readDrawerSignature !== 'function' ||
        typeof dispatchRefresh !== 'function'
      ) {
        return 'missing';
      }

      let expected = cartSignature(await readCart());
      if (!isCurrent(options.generation)) return 'stale';

      // Trove/Kite normally refreshes the drawer itself. Give that native path
      // up to 2.5 seconds before considering a fallback refresh.
      for (let check = 0; check < maxChecks; check += 1) {
        if (!isCurrent(options.generation)) return 'stale';
        if (readDrawerSignature() === expected) return 'native';
        if (check < maxChecks - 1) await wait(intervalMs);
      }

      // Re-read the server cart before fallback so a stale initial response
      // cannot cause an unnecessary second drawer refresh.
      expected = cartSignature(await readCart());
      if (!isCurrent(options.generation)) return 'stale';
      if (readDrawerSignature() === expected) return 'native';

      dispatchRefresh();
      return 'fallback';
    } catch (error) {
      return 'error';
    }
  }

  async function syncCartPage(dependencies) {
    const options = dependencies || {};
    const isCurrent = options.isCurrent;

    try {
      if (typeof isCurrent !== 'function' || !isCurrent(options.generation)) return 'stale';
      if (
        typeof options.getMainCart !== 'function' ||
        typeof options.readCart !== 'function' ||
        typeof options.readSection !== 'function'
      ) {
        return 'missing';
      }

      const mainCart = options.getMainCart();
      const sectionId = mainCartSectionId(mainCart);
      if (!mainCart || !sectionId) return 'missing';

      // Fetch the latest cart and the runtime Main Cart section together. The
      // section id is dynamic, so it is read from the current DOM.
      const responses = await Promise.all([options.readCart(), options.readSection(sectionId)]);
      if (!isCurrent(options.generation)) return 'stale';

      const cart = responses[0];
      const sectionHtml = responses[1];
      if (!cart || typeof sectionHtml !== 'string' || !sectionHtml.trim()) return 'error';

      cart.sections = cart.sections || {};
      cart.sections[sectionId] = sectionHtml;

      if (typeof options.publishCart === 'function' && options.publishCart(cart)) return 'published';
      if (
        typeof options.replaceMainCart === 'function' &&
        options.replaceMainCart(mainCart, sectionHtml, cart)
      ) {
        return 'replaced';
      }

      return 'missing';
    } catch (error) {
      return 'error';
    }
  }

  function storefrontRoot(win) {
    const rootUrl = win && win.Shopify && win.Shopify.routes && win.Shopify.routes.root;
    const normalized = typeof rootUrl === 'string' && rootUrl ? rootUrl : '/';
    return normalized.slice(-1) === '/' ? normalized : normalized + '/';
  }

  async function fetchJson(win, url) {
    const response = await win.fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  async function fetchText(win, url) {
    const response = await win.fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Section request failed');
    return response.text();
  }

  function readDrawerSignature(doc) {
    const drawer = doc.querySelector('cart-drawer[data-section-id]');
    if (!drawer) return null;

    const sectionId = drawer.getAttribute('data-section-id');
    const body = sectionId ? doc.getElementById('CartDrawerBody-' + sectionId) : null;
    if (!body) return null;

    return itemSignature(
      Array.from(body.querySelectorAll('.cart-item[data-variant-id][data-quantity]')).map(function (item) {
        return {
          variantId: item.getAttribute('data-variant-id'),
          quantity: item.getAttribute('data-quantity'),
        };
      })
    );
  }

  function publishCart(win, cart) {
    const pubsub = win && win.FoxTheme && win.FoxTheme.pubsub;
    const eventName = pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartUpdate;
    if (!pubsub || typeof pubsub.publish !== 'function' || !eventName) return false;

    // Prefer Trove's public cart event so its own components perform the redraw.
    pubsub.publish(eventName, { cart: cart });
    return true;
  }

  function replaceMainCart(win, doc, mainCart, sectionHtml, cart) {
    if (!win.DOMParser) return false;

    const parsed = new win.DOMParser().parseFromString(sectionHtml, 'text/html');
    const replacementMainCart = parsed.getElementById(mainCart.id);
    const currentCartContainer = mainCart.closest('.cart');
    const replacementCartContainer = replacementMainCart && replacementMainCart.closest('.cart');
    if (!currentCartContainer || !replacementCartContainer) return false;

    // Fallback only when Trove pub/sub is unavailable: replace the returned
    // server-rendered cart container without changing cart data ourselves.
    currentCartContainer.replaceWith(replacementCartContainer);
    doc.dispatchEvent(new win.CustomEvent('cart:updated', { detail: { cart: cart } }));
    return true;
  }

  function install(win, doc) {
    // Install once even if Shopify's editor or app scripts evaluate this asset
    // more than once.
    if (!win || !doc || win.__cozyKiteCartSyncInstalled) return false;
    win.__cozyKiteCartSyncInstalled = true;

    // Every new Kite mutation invalidates older requests/waits. This prevents a
    // slow response from restoring an earlier cart state.
    let currentGeneration = 0;
    const isCurrent = function (generation) {
      return generation === currentGeneration;
    };

    // No polling or global fetch interception: work starts only from the
    // explicit post-mutation event dispatched by Kite's configured hook.
    doc.addEventListener(EVENT_NAME, function () {
      currentGeneration += 1;
      const generation = currentGeneration;
      const rootUrl = storefrontRoot(win);
      const getMainCart = function () {
        return doc.querySelector('main-cart[id^="MainCart-"]');
      };

      Promise.resolve().then(async function () {
        if (!isCurrent(generation)) return;

        if (getMainCart()) {
          await syncCartPage({
            generation: generation,
            isCurrent: isCurrent,
            getMainCart: getMainCart,
            readCart: function () {
              return fetchJson(win, rootUrl + 'cart.js');
            },
            readSection: function (sectionId) {
              return fetchText(win, rootUrl + '?section_id=' + encodeURIComponent(sectionId));
            },
            publishCart: function (cart) {
              return publishCart(win, cart);
            },
            replaceMainCart: function (mainCart, sectionHtml, cart) {
              return replaceMainCart(win, doc, mainCart, sectionHtml, cart);
            },
          });
          return;
        }

        if (!doc.querySelector('cart-drawer[data-section-id]')) return;

        // Drawer fallback remains fail-closed: errors or changed selectors end
        // the task without adding products or altering offer eligibility.
        await syncDrawer({
          generation: generation,
          isCurrent: isCurrent,
          readCart: function () {
            return fetchJson(win, rootUrl + 'cart.js');
          },
          readDrawerSignature: function () {
            return readDrawerSignature(doc);
          },
          wait: sleep,
          dispatchRefresh: function () {
            doc.dispatchEvent(
              new win.CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { open: true, source: 'kite-cart-sync' },
              })
            );
          },
        });
      });
    });

    return true;
  }

  return {
    EVENT_NAME: EVENT_NAME,
    cartSignature: cartSignature,
    itemSignature: itemSignature,
    mainCartSectionId: mainCartSectionId,
    syncDrawer: syncDrawer,
    syncCartPage: syncCartPage,
    install: install,
  };
});
